import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import {
  Search,
  Calendar,
  Clock,
  Tag,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  BookOpen
} from "lucide-react";
import { Helmet } from "react-helmet-async";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Same reveal hook the home/about/FAQ pages use (tiny threshold so tall
// sections still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "popular", label: "Popular" },
];

export default function Blog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  // Filters from URL
  const currentPage = parseInt(searchParams.get("page") || "1");
  const currentCategory = searchParams.get("category") || "";
  const currentSearch = searchParams.get("search") || "";
  const currentTag = searchParams.get("tag") || "";
  const currentSort = searchParams.get("sort") || "newest";

  const [searchInput, setSearchInput] = useState(currentSearch);

  const [heroRef, heroInView] = useInView();
  const [gridRef, gridInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Sync searchInput with URL search param when it changes
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  useEffect(() => {
    loadPosts();
    loadCategories();
  }, [currentPage, currentCategory, currentSearch, currentTag, currentSort]);

  const loadPosts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "9",
        sort: currentSort
      });

      if (currentCategory) params.append("category", currentCategory);
      if (currentSearch) params.append("search", currentSearch);
      if (currentTag) params.append("tag", currentTag);

      const response = await fetch(`${BACKEND_URL}/api/blog/posts?${params}`);
      const data = await response.json();

      if (data.success) {
        setPosts(data.posts);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Error loading posts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const updateFilters = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    // Reset to page 1 when filters change (except page itself)
    if (!("page" in updates)) {
      newParams.set("page", "1");
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ search: searchInput });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const chipClass = (active) =>
    `px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
      active
        ? "bg-slate-900 text-white border-slate-900"
        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
    }`;

  return (
    <>
      <Helmet>
        <title>Blog - Pay Stub Tips, Payroll Guides & Financial Documentation | MintSlip</title>
        <meta name="description" content="Expert guides on pay stubs, proof of income, payroll management, and financial documentation. Learn how to create professional pay stubs and manage your finances." />
        <meta property="og:title" content="MintSlip Blog - Pay Stub & Payroll Guides" />
        <meta property="og:description" content="Expert guides on pay stubs, proof of income, and financial documentation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mintslip.com/blog" />
        <meta property="og:image" content="https://mintslip.com/og-image.png" />
        <meta property="og:site_name" content="MintSlip" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="MintSlip Blog - Pay Stub & Payroll Guides" />
        <meta name="twitter:description" content="Expert guides on pay stubs, proof of income, and financial documentation." />
        <meta name="twitter:image" content="https://mintslip.com/og-image.png" />
        <link rel="canonical" href="https://mintslip.com/blog" />
      </Helmet>

      {/* overflowX clip matches the home page root — the sticky navbar pill
          runs a few px wide on small phones and would otherwise cause a
          horizontal page scroll. */}
      <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Header title="MintSlip" />

      {/* Hero — centered display type like the home/about/FAQ heroes */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-8 md:pt-20 md:pb-10">
        <div className={`text-center transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 text-sm font-medium text-emerald-900 mb-6">
            <BookOpen className="w-4 h-4 text-emerald-700" />
            The MintSlip Blog
          </div>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Guides &amp; <span className="font-black">resources</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Expert guides, tips, and resources for pay stubs, proof of income, payroll, and financial documentation.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-14 pl-12 pr-4 text-base rounded-2xl border border-slate-200 shadow-sm text-slate-800 bg-white"
            />
          </form>
        </div>
      </section>

      {/* Filters — pill chips in place of dropdowns */}
      <section className="max-w-[1288px] mx-auto px-6 pb-8">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button className={chipClass(!currentCategory)} onClick={() => updateFilters({ category: "" })}>
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              className={chipClass(currentCategory === cat.slug)}
              onClick={() => updateFilters({ category: currentCategory === cat.slug ? "" : cat.slug })}
            >
              {cat.name}
            </button>
          ))}
          <span className="hidden sm:block w-px h-6 bg-slate-200 mx-2" />
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={chipClass(currentSort === opt.value)}
              onClick={() => updateFilters({ sort: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Active search / tag clearers */}
        {(currentSearch || currentTag) && (
          <div className="flex items-center justify-center gap-2 mt-4">
            {currentSearch && (
              <button
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 border border-emerald-200/80 text-emerald-900 text-sm font-medium rounded-full hover:bg-emerald-100 transition-colors"
                onClick={() => { setSearchInput(""); updateFilters({ search: "" }); }}
              >
                <Search className="w-3.5 h-3.5" />
                {currentSearch}
                <span className="ml-0.5">×</span>
              </button>
            )}
            {currentTag && (
              <button
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 border border-emerald-200/80 text-emerald-900 text-sm font-medium rounded-full hover:bg-emerald-100 transition-colors"
                onClick={() => updateFilters({ tag: "" })}
              >
                <Tag className="w-3.5 h-3.5" />
                {currentTag}
                <span className="ml-0.5">×</span>
              </button>
            )}
          </div>
        )}
      </section>

      {/* Posts Grid */}
      <section ref={gridRef} className={`max-w-[1288px] mx-auto px-6 pb-16 transition-all duration-700 ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-14 bg-slate-100 rounded-[28px]">
            <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-5">
              <FileText className="w-7 h-7 text-slate-900" />
            </span>
            <p className="text-slate-600 text-lg">No articles found</p>
            <p className="text-slate-500 text-sm mt-2">
              {currentSearch || currentCategory
                ? "Try adjusting your filters"
                : "Check back soon for new content!"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  {/* Featured Image */}
                  {post.featuredImage ? (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img
                        src={post.featuredImage.startsWith('/') ? `${BACKEND_URL}${post.featuredImage}` : post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-100 flex items-center justify-center">
                      <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                        <FileText className="w-7 h-7 text-slate-900" />
                      </span>
                    </div>
                  )}

                  <div className="p-5">
                    {/* Category */}
                    {post.category && (
                      <span className="inline-block px-3 py-1 bg-emerald-50 border border-emerald-200/80 text-emerald-900 text-xs font-medium rounded-full mb-3">
                        {categories.find(c => c.slug === post.category)?.name || post.category}
                      </span>
                    )}

                    {/* Title */}
                    <h2 className="font-display text-lg font-bold tracking-tight text-slate-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.publishDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readingTime || 5} min read
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  aria-label="Previous page"
                  disabled={currentPage <= 1}
                  onClick={() => updateFilters({ page: (currentPage - 1).toString() })}
                  className="w-11 h-11 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:border-slate-400 transition-colors disabled:opacity-40 disabled:hover:border-slate-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="text-sm text-slate-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  aria-label="Next page"
                  disabled={currentPage >= totalPages}
                  onClick={() => updateFilters({ page: (currentPage + 1).toString() })}
                  className="w-11 h-11 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:border-slate-400 transition-colors disabled:opacity-40 disabled:hover:border-slate-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Final CTA — same treatment as the home/FAQ closers */}
      <section ref={ctaRef} className="max-w-[1288px] mx-auto px-6 pb-20 md:pb-28">
        <div className={`rounded-[28px] bg-slate-100 px-6 py-14 md:py-20 text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Put these guides to <span className="font-black">work</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Generate accurate, professional documents in minutes. Instant download. No sign-up required.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={() => navigate("/app")}>
              <FileText size={18} />
              <span>Create a document</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </>
  );
}
